import { Button, Col, Container, Dropdown, ListGroup, Modal, Row } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
  BeamIcon,
  CopyIcon,
  DownloadIcon,
  ExpandToggleIcon,
  FoundationIcon,
  ColumnIcon,
  FrameIcon,
  PrintIcon,
  SectionIcon,
  TableIcon,
  ShareIcon,
  TrashIcon,
} from "@simplex/sx-icons";
import FilePanelHeaderToolbar from "../ui-components/FilePanelHeaderToolbar";
import FilePanelListToolbarView from "../ui-components/FilePanelListToolbarView";
import { AppType } from "@common/types/sharedTypes";
import { getAppTypeLabel as appTypeLabelFor } from "../../helpers/appTypes";
import { resetFile } from "../../features/user/userSlice";
import { openModal } from "../../features/ui/uiSlice";
import { useRangeAndToggleSelection } from "../../hooks/useRangeAndToggleSelection";
import type { ProjectFile } from "../../ProjectExplorer";
import type { CenterPanelProps } from "./panelProps";

export default function CenterPanel(props: CenterPanelProps) {
  const {
    selectedProjectName,
    user,
    dropHandler,
    dragOverHandler,
    dragEnterHandler,
    dragLeaveHandler,
    t,
    sfileStatus,
    setSfileStatus,
    updateActiveDeactive,
    updateActiveDeactiveProject,
    getUser,
    refreshSelectedProject,
    showProjectDeleteModal,
    setShowProjectDeleteModal,
    deleteProjectandFiles,
    isDragging,
    isFilesDragOver,
    isCreatingNew,
    setCopyOfCurrentSelectedFile,
    access,
    config,
    uploadProjectFile,
    setProjectName,
    parseUTC,
    parseAppType,
    setDraggedOverFile,
    selectedFile,
    setSelectedFile,
    setNextFileListName,
    setKeywordInputDirty,
    copyOFCurrentSelectedFile,
    dispatch,
    setCheckedCount,
    selectedFileIds,
    setSelectedFileIds,
    onDeleteSelectedFiles,
    downloadProject,
    handleFramePrint,
    handlePrint,
    handleBatchPrint,
    copyProject,
    isPrinting,
  } = props;

  const currentFile = user.projectdigest[selectedProjectName]?.files[selectedFile] ?? null;
  const currentFileAppType = parseAppType(currentFile?.type);
  const hasSelection = selectedFileIds.length > 1;
  const deleteCandidateIds = selectedFileIds.length > 0 ? selectedFileIds : currentFile?.id ? [currentFile.id] : [];
  const canShowFileActions = !!currentFile;
  const canShowBatchPrintAction = selectedFileIds.length >= 2;
  const showPrintActions = !!config.IS_DEV;
  const showShareAction = !!config.IS_DEV;
  const isProjectFileAreaDisabled = user.projectdigest[selectedProjectName]?.status === "inactive" || isCreatingNew;
  const {
    setSelectionAnchorId,
    applyRangeSelection,
    toggleSelection: toggleFileSelection,
  } = useRangeAndToggleSelection<ProjectFile>({
    setSelectedIds: setSelectedFileIds,
    focusedId: currentFile?.id ?? null,
    onSelectionChange: (selectedIds) => setCheckedCount(selectedIds.length),
  });

  const focusFile = (file: ProjectFile, realIndex: number, nextProjectName: string) => {
    setSelectedFile(realIndex);
    setNextFileListName(nextProjectName);
    setKeywordInputDirty(false);

    if (copyOFCurrentSelectedFile) {
      dispatch(
        resetFile({
          file: copyOFCurrentSelectedFile,
          selectedProjectName: selectedProjectName,
          index: selectedFile,
        }),
      );
    }

    setCopyOfCurrentSelectedFile({ ...file });
  };

  const getAppTypeLabel = (file: ProjectFile) => appTypeLabelFor(parseAppType(file.type));

  // Mirrors the "Open file" button logic in RightPanel so double-clicking a row opens the
  // same URL that the enabled Open button would navigate to.
  const getFileOpenUrl = (file: ProjectFile): string | null => {
    if (!file.guid) return null;
    const appType = parseAppType(file.type);

    switch (appType) {
      case AppType.SIMPLEX_APP_BEAM:
        return access["beam-app"] === 200 ? `${config.BEAM_URL}files/${file.guid}` : null;
      case AppType.SIMPLEX_APP_FOUNDATION:
        return access["foundation-app"] === 200 ? `${config.FOUNDATION_URL}files/${file.guid}` : null;
      case AppType.SIMPLEX_APP_COLUMN:
        return access["column-app"] === 200 ? `${config.COLUMN_URL}files/${file.guid}` : null;
      case AppType.SIMPLEX_APP_FRAME:
        return access["frame-app"] === 200 ? `${config.FRAME_URL}files/${file.guid}/frame` : null;
      case AppType.SIMPLEX_APP_SECTION:
        return access["section-app"] === 200 ? `${config.FRAME_URL}files/${file.guid}/section` : null;
      case AppType.SIMPLEX_APP_RETAININGWALL:
        return access["retainingwall-app"] === 200 ? `${config.RETAININGWALL_URL}files/${file.guid}` : null;
      case AppType.SIMPLEX_APP_BASEMENT_WALL:
        return access["basementwall-app"] === 200 ? `${config.BASEMENTWALL_URL}files/${file.guid}` : null;
      case AppType.SIMPLEX_APP_PILE:
        return access["pile-app"] === 200 ? `${config.PILE_URL}files/${file.guid}` : null;
      case AppType.SIMPLEX_APP_CPT:
        return access["cpt-app"] === 200 ? `${config.CPT_URL}files/${file.guid}` : null;
      default:
        return null;
    }
  };

  const openFile = (file: ProjectFile) => {
    const url = getFileOpenUrl(file);
    if (url) window.open(url, "_blank");
  };

  return (
    <Col md={6} className="pt-0 pb-3 pt-2 ps-2 pe-2 h-100">
      <div id="middle-panel" className="d-flex flex-column h-100" style={{ overflow: "hidden" }}>
        {user.projectdigest[selectedProjectName] && (
          <>
            <Modal
              className="sxmodal"
              show={showProjectDeleteModal}
              onHide={() => setShowProjectDeleteModal(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title>
                  {t("delete_modal.confirm_delete_title") || "Confirm Deletion"}
                  {": "}
                  {selectedProjectName}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p>
                  {t("delete_modal.confirm_delete_message")} <b>{selectedProjectName}</b>?
                </p>
                <p>{t("following_files_will_be_deleted_")}</p>
                <ul>
                  {user.projectdigest[selectedProjectName].files.map((f: ProjectFile) => (
                    <li key={f.guid}>{f.name}</li>
                  ))}
                </ul>
              </Modal.Body>
              <Modal.Footer>
                <Button
                  variant="secondary"
                  className="action-btn-transparent"
                  onClick={() => setShowProjectDeleteModal(false)}
                >
                  {t("buttons.cancel") || "Cancel"}
                </Button>
                <Button
                  variant="danger"
                  className="action-btn"
                  onClick={async () => {
                    await deleteProjectandFiles(user.projectdigest[selectedProjectName]?.guid || "");
                    await getUser();
                    setShowProjectDeleteModal(false);
                  }}
                >
                  {t("buttons.delete") || "Delete"}
                </Button>
              </Modal.Footer>
            </Modal>
          </>
        )}

        {/* Keep horizontal padding aligned with LeftPanel to make spacing maintenance predictable across panels. */}
        {user.projectdigest[selectedProjectName] ? (
          <>
            <Row className="mb-3">
              <Col className="pt-2">
                <FilePanelHeaderToolbar
                  id="project-list-toolbar"
                  headerTitle={user.projectdigest[selectedProjectName]?.name || t("project_files")}
                  showStatusDropdown={Boolean(user.projectdigest[selectedProjectName]?.status)}
                  statusValue={sfileStatus}
                  onStatusChange={async (newStatus) => {
                    const list: Array<{ guid: string; type: AppType; active: boolean }> =
                      user.projectdigest[selectedProjectName]?.files.map((file: ProjectFile) => ({
                        guid: file.guid || "",
                        type: parseAppType(file.type) ?? AppType.SIMPLEX_APP_BEAM,
                        active: newStatus === "active",
                      })) ?? [];

                    await updateActiveDeactive(list);
                    const projectGUid = user.projectdigest[selectedProjectName || ""]?.guid;
                    await updateActiveDeactiveProject(projectGUid, newStatus === "active");

                    await getUser();
                    setSfileStatus(newStatus);
                    refreshSelectedProject();
                  }}
                  disableFileActions={Boolean(isProjectFileAreaDisabled)}
                  setCopyOfCurrentSelectedFile={setCopyOfCurrentSelectedFile}
                  buttonTitle={t("new_file")}
                  className="mb-4"
                  beamAccess={access["beam-app"] == 200}
                  foundationAccess={access["foundation-app"] == 200}
                  columnAccess={access["column-app"] == 200}
                  frameAccess={config.IS_DEV || access["frame-app"] == 200}
                  sectionAccess={config.IS_DEV || access["section-app"] == 200}
                  retainingWallAccess={config.IS_DEV || access["retainingwall-app"] == 200}
                  basementWallAccess={config.IS_DEV || access["basementwall-app"] == 200}
                  pileAccess={config.IS_DEV || access["pile-app"] == 200}
                  cptAccess={config.IS_DEV || access["cpt-app"] == 200}
                  selectedProjecteName={selectedProjectName || ""}
                  items={Object.values(user.projectdigest || {})}
                  uploadProjectFile={(file: File) => {
                    const projectGUid = user.projectdigest[selectedProjectName || ""]?.guid;
                    uploadProjectFile(
                      file,
                      selectedProjectName ? selectedProjectName : null,
                      projectGUid ? projectGUid : null,
                      (name: string) => {
                        setProjectName(name);
                      },
                    );
                  }}
                  setSelectedFileName={setProjectName}
                  renderToolbarActions={() =>
                    config.IS_DEV &&
                    showShareAction && (
                      <button
                        className="search-toggle-icon secondary-btn"
                        title={t("buttons.share")}
                        onClick={() => {
                          dispatch(openModal("shareModal"));
                        }}
                      >
                        <ShareIcon color="currentColor" size={16} />
                      </button>
                    )
                  }
                />
              </Col>
            </Row>

            <div
              id="project-file-list"
              onDrop={dropHandler}
              onDragOver={dragOverHandler}
              onDragEnter={dragEnterHandler}
              onDragLeave={dragLeaveHandler}
              className={`shadow-sm drop-zone ${isDragging && isFilesDragOver ? "drag-over" : ""}`}
              style={isProjectFileAreaDisabled ? { backgroundColor: "#eee", opacity: 0.6, pointerEvents: "none" } : {}}
            >
              <Row className="flex-grow-1" style={{ minHeight: 0 }}>
                <Col className="h-100" style={{ minHeight: 0, padding: 0 }}>
                  <FilePanelListToolbarView
                    id="project-file-list"
                    className="h-100"
                    hideFilterButtonWhenHeaderProvided={false}
                    items={user.projectdigest[selectedProjectName]?.files || []}
                    showDeleteButton
                    deleteButtonDisabled={deleteCandidateIds.length === 0}
                    deleteButtonTitle={t("buttons.delete_multiple")}
                    onDeleteButtonClick={onDeleteSelectedFiles}
                    renderToolbarActions={() => (
                      <>
                        {canShowFileActions && (
                          <>
                            <button
                              className="search-toggle-icon primary-btn"
                              title={t("buttons.open_file")}
                              disabled={hasSelection || !currentFile || !getFileOpenUrl(currentFile)}
                              onClick={() => {
                                if (currentFile) openFile(currentFile);
                              }}
                            >
                              {t("buttons.open_file")}
                            </button>

                            <button
                              className="search-toggle-icon secondary-btn"
                              title={t("download_file")}
                              disabled={currentFileAppType === null || hasSelection}
                              onClick={() => {
                                const id = currentFile?.guid ?? null;
                                if (id && currentFileAppType !== null) {
                                  downloadProject(id, currentFileAppType, currentFile?.name || "project");
                                }
                              }}
                            >
                              <DownloadIcon color="currentColor" size={18} />
                            </button>

                            {showPrintActions &&
                              !canShowBatchPrintAction &&
                              ((currentFileAppType === AppType.SIMPLEX_APP_FRAME && access["frame-app"] === 200) ||
                                (currentFileAppType === AppType.SIMPLEX_APP_SECTION &&
                                  access["section-app"] === 200)) && (
                                <Dropdown>
                                  <Dropdown.Toggle className="search-toggle-icon secondary-btn" disabled={hasSelection}>
                                    <PrintIcon color="currentColor" strokeWidth={1.6} size={16} />
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => handleFramePrint("minimum")}>
                                      {t("print_extent_minimum")}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleFramePrint("normal")}>
                                      {t("print_extent_normal")}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleFramePrint("extended")}>
                                      {t("print_extent_extended")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              )}

                            {showPrintActions &&
                              !canShowBatchPrintAction &&
                              currentFileAppType !== AppType.SIMPLEX_APP_FRAME &&
                              currentFileAppType !== AppType.SIMPLEX_APP_SECTION &&
                              currentFileAppType !== null && (
                                <Dropdown>
                                  <Dropdown.Toggle
                                    className="search-toggle-icon secondary-btn"
                                    disabled={isPrinting || hasSelection}
                                  >
                                    {isPrinting ? (
                                      <FontAwesomeIcon icon={faSpinner} spin={isPrinting} />
                                    ) : (
                                      <PrintIcon color="currentColor" strokeWidth={1.6} size={16} />
                                    )}
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => handlePrint("reduced")} disabled={isPrinting}>
                                      {t("print_extent_minimum")}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handlePrint("normal")} disabled={isPrinting}>
                                      {t("print_extent_normal")}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handlePrint("full")} disabled={isPrinting}>
                                      {t("print_extent_extended")}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              )}

                            {showPrintActions && canShowBatchPrintAction && (
                              <Dropdown title={t("print_file")}>
                                <Dropdown.Toggle className="search-toggle-icon secondary-btn" disabled={isPrinting}>
                                  {isPrinting ? (
                                    <FontAwesomeIcon icon={faSpinner} spin={isPrinting} />
                                  ) : (
                                    <PrintIcon color="currentColor" size={16} />
                                  )}
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                  <Dropdown.Item onClick={() => handleBatchPrint("reduced")} disabled={isPrinting}>
                                    {t("print_extent_minimum")}
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleBatchPrint("normal")} disabled={isPrinting}>
                                    {t("print_extent_normal")}
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleBatchPrint("full")} disabled={isPrinting}>
                                    {t("print_extent_extended")}
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                              </Dropdown>
                            )}

                            {currentFileAppType !== null &&
                              ((access["beam-app"] == 200 && currentFileAppType == AppType.SIMPLEX_APP_BEAM) ||
                                (access["column-app"] == 200 && currentFileAppType == AppType.SIMPLEX_APP_COLUMN) ||
                                (access["foundation-app"] == 200 &&
                                  currentFileAppType == AppType.SIMPLEX_APP_FOUNDATION) ||
                                (access["frame-app"] == 200 && currentFileAppType == AppType.SIMPLEX_APP_FRAME) ||
                                (access["section-app"] == 200 && currentFileAppType == AppType.SIMPLEX_APP_SECTION) ||
                                ((config.IS_DEV || access["retainingwall-app"] == 200) &&
                                  currentFileAppType == AppType.SIMPLEX_APP_RETAININGWALL) ||
                                ((config.IS_DEV || access["basementwall-app"] == 200) &&
                                  currentFileAppType == AppType.SIMPLEX_APP_BASEMENT_WALL) ||
                                ((config.IS_DEV || access["pile-app"] == 200) &&
                                  currentFileAppType == AppType.SIMPLEX_APP_PILE) ||
                                ((config.IS_DEV || access["cpt-app"] == 200) &&
                                  currentFileAppType == AppType.SIMPLEX_APP_CPT)) && (
                                <button
                                  className="search-toggle-icon secondary-btn"
                                  title={t("copy_file")}
                                  disabled={hasSelection}
                                  onClick={async () => {
                                    await copyProject(
                                      currentFile?.id + "" || "",
                                      currentFile?.guid || "",
                                      currentFileAppType as AppType,
                                      (name: string) => {
                                        setProjectName(name);
                                      },
                                    );
                                  }}
                                >
                                  <CopyIcon color="currentColor" size={18} />
                                </button>
                              )}
                          </>
                        )}
                      </>
                    )}
                    renderListHeader={(headerSort) => {
                      const renderSortableHeader = (
                        label: string,
                        sortKey: string,
                        initialDirection: "asc" | "desc" = "asc",
                      ) => {
                        const direction = headerSort.getDirectionFor(sortKey);
                        const indicator = direction === "asc" ? "▲" : direction === "desc" ? "▼" : "↕";
                        const isActive = direction !== null;

                        return (
                          <button
                            type="button"
                            className={`project-file-header-button ${isActive ? "active" : ""}`}
                            onClick={() => headerSort.cycleSortBy(sortKey, initialDirection)}
                          >
                            <span>{label}</span>
                            <span className="project-file-header-sort-indicator">{indicator}</span>
                          </button>
                        );
                      };

                      return (
                        <Container fluid className="project-file-list-header px-3 py-2">
                          <Row className="project-file-row align-items-center gx-2">
                            <Col xs={3} className="fw-semibold text-uppercase text-muted small">
                              {renderSortableHeader(t("meta_data.name"), "name")}
                            </Col>
                            <Col xs={1} className="fw-semibold text-uppercase text-muted small">
                              {renderSortableHeader(t("meta_data.author"), "author")}
                            </Col>
                            <Col xs={2} className="fw-semibold text-uppercase text-muted small ">
                              {renderSortableHeader("Utilization", "utilization", "desc")}
                            </Col>
                            <Col xs={2} className="fw-semibold text-uppercase text-muted small">
                              {renderSortableHeader("Type", "type")}
                            </Col>
                            <Col xs={2} className="fw-semibold text-uppercase text-muted small">
                              {t("tags")}
                            </Col>
                            <Col
                              xs={2}
                              style={{ textAlign: "right" }}
                              className="fw-semibold text-uppercase text-muted small"
                            >
                              {renderSortableHeader(t("last_modified"), "latestModified", "desc")}
                            </Col>
                          </Row>
                        </Container>
                      );
                    }}
                    getType={(file: ProjectFile) => String(file.type ?? "")}
                    getSortValue={(file: ProjectFile, mode: string) => {
                      if (mode === "latestModified") {
                        return parseUTC(file.lastModified).getTime();
                      }

                      if (mode === "author") {
                        return file.author ?? "";
                      }

                      if (mode === "utilization") {
                        return typeof file.maxUtilizationPercent === "number" &&
                          Number.isFinite(file.maxUtilizationPercent)
                          ? file.maxUtilizationPercent
                          : -1;
                      }

                      if (mode === "type") {
                        return getAppTypeLabel(file);
                      }

                      return file.name ?? "";
                    }}
                    getSearchTerms={(file: ProjectFile) => {
                      let terms = [file.name || ""];
                      if (file.keywords) {
                        if (Array.isArray(file.keywords)) {
                          terms = terms.concat(file.keywords.filter((k) => typeof k === "string"));
                        } else if (typeof file.keywords === "string") {
                          terms = terms.concat((file.keywords as string).split(",").map((k) => k.trim()));
                        }
                      }
                      return terms;
                    }}
                    renderItem={(
                      file: ProjectFile,
                      _index: number,
                      realIndex: number,
                      nextProjectName: string,
                      visibleItems: ProjectFile[],
                    ) => (
                      <ListGroup.Item
                        as="li"
                        draggable={true}
                        key={file.id}
                        onDragStart={(e) => {
                          e.dataTransfer.setData("application/x-item-id", file.id.toString());
                          setDraggedOverFile(file);
                        }}
                        data-guid={file.guid}
                        style={{ cursor: "pointer" }}
                        className={
                          selectedFileIds.includes(file.id) || realIndex === selectedFile ? "selected-file" : ""
                        }
                        onClick={(e) => {
                          focusFile(file, realIndex, nextProjectName);

                          // Shift-click extends selection from the last anchor to the clicked row.
                          if (e.shiftKey) {
                            applyRangeSelection(visibleItems, file);
                            return;
                          }

                          // Cmd on macOS / Ctrl on Windows/Linux toggles a single row in the current selection.
                          if (e.metaKey || e.ctrlKey) {
                            setSelectionAnchorId(file.id);
                            toggleFileSelection(file.id);
                            return;
                          }

                          // Plain click focuses one file and resets any bulk selection state.
                          setSelectionAnchorId(file.id);
                          setSelectedFileIds([]);
                          setCheckedCount(0);
                        }}
                        onDoubleClick={() => {
                          focusFile(file, realIndex, nextProjectName);
                          openFile(file);
                        }}
                      >
                        <Container fluid className="drag-container p-0">
                          <Row className="project-file-row align-items-center p-2">
                            <Col xs={3}>{file.name}</Col>

                            <Col xs={1} className="text-truncate">
                              {file.author && (
                                <span className="text-muted" style={{ fontSize: 12 }} title={file.author}>
                                  {file.author}
                                </span>
                              )}
                            </Col>

                            <Col xs={2}>
                              {(() => {
                                const utilizationPercent =
                                  typeof file.maxUtilizationPercent === "number" &&
                                  Number.isFinite(file.maxUtilizationPercent)
                                    ? file.maxUtilizationPercent
                                    : null;
                                const utilizationColor =
                                  utilizationPercent === null
                                    ? "#9aa0a6"
                                    : utilizationPercent <= 100
                                      ? "#1f9d55"
                                      : "#d93025";
                                const utilizationTitle =
                                  utilizationPercent === null
                                    ? t("utilization.no_controls_found")
                                    : t("utilization.max_utilization", { utilizationPercent });

                                return (
                                  <span
                                    className="utilization-indicator"
                                    title={utilizationTitle}
                                    style={{
                                      color: utilizationColor,
                                      fontWeight: 600,
                                      fontSize: "1.275em",
                                    }}
                                  >
                                    {utilizationPercent !== null ? `${utilizationPercent}%` : "—"}
                                  </span>
                                );
                              })()}
                            </Col>

                            <Col xs={2} className="p-0">
                              {(() => {
                                const appType = parseAppType(file.type);
                                const label = getAppTypeLabel(file);

                                const renderTypeCell = (icon: React.ReactNode) => (
                                  <span className="type-cell" title={label}>
                                    <span className="badge type-cell-icon">{icon}</span>
                                    <span className="badge type-cell-label">{label}</span>
                                  </span>
                                );

                                if (appType === AppType.SIMPLEX_APP_BEAM)
                                  return renderTypeCell(<BeamIcon color="gray" />);
                                if (appType === AppType.SIMPLEX_APP_FOUNDATION)
                                  return renderTypeCell(<FoundationIcon color="gray" />);
                                if (appType === AppType.SIMPLEX_APP_COLUMN)
                                  return renderTypeCell(<ColumnIcon color="gray" size={24} />);
                                if (appType === AppType.SIMPLEX_APP_FRAME)
                                  return renderTypeCell(<FrameIcon color="gray" />);
                                if (appType === AppType.SIMPLEX_APP_SECTION)
                                  return renderTypeCell(<SectionIcon color="gray" />);
                                // No dedicated icons exist yet for the geotechnical app types, so the
                                // closest existing icon stands in for each until one is added.
                                if (appType === AppType.SIMPLEX_APP_RETAININGWALL)
                                  return renderTypeCell(<FoundationIcon color="gray" />);
                                if (appType === AppType.SIMPLEX_APP_BASEMENT_WALL)
                                  return renderTypeCell(<FoundationIcon color="gray" />);
                                if (appType === AppType.SIMPLEX_APP_PILE)
                                  return renderTypeCell(<ColumnIcon color="gray" size={24} />);
                                if (appType === AppType.SIMPLEX_APP_CPT)
                                  return renderTypeCell(<TableIcon color="gray" />);

                                return (
                                  <span className="type-cell" title="Unknown">
                                    <span className="badge type-cell-label">UNKNOWN</span>
                                  </span>
                                );
                              })()}
                            </Col>
                            <Col xs={2}>
                              {(() => {
                                const keywords: string[] = Array.isArray(file.keywords)
                                  ? file.keywords
                                      .filter((k): k is string => typeof k === "string")
                                      .map((k) => k.trim())
                                      .filter((k) => k.length > 0 && !k.startsWith("{"))
                                  : [];

                                if (keywords.length === 0) return null;

                                return (
                                  <div className="pill-wrapper">
                                    {keywords.map((keyword) => (
                                      <span
                                        key={keyword}
                                        title={keyword}
                                        className="pill-container pill-container-static"
                                      >
                                        <span className="pill-text">{keyword}</span>
                                      </span>
                                    ))}
                                  </div>
                                );
                              })()}
                            </Col>

                            <Col xs={2} style={{ textAlign: "right" }}>
                              {file.lastModified && (
                                <span className="text-muted ms-2">
                                  {parseUTC(file.lastModified).toLocaleDateString()}
                                </span>
                              )}
                            </Col>
                          </Row>
                        </Container>
                      </ListGroup.Item>
                    )}
                  />
                </Col>
              </Row>
            </div>
          </>
        ) : (
          <p style={{ fontSize: 12 }}>{t("select_a_project_to_view_its_files")}</p>
        )}
      </div>
    </Col>
  );
}
